import { Form, Radio } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React, { forwardRef, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  emailValidator,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import { duplicateNameValidator } from "utils/validatorUtil";

import ZenDeskConfigMappingForm from "./ZenDeskConfigMappingForm";

type ZendeskConfigLoginFormProps = {
  name: string;
  loginConfigList: any[];
  handleMapping: (response: any) => void;
  config: any;
  onUpdateConfigComponent: (shouldUpdate?: boolean) => void;
  afterClose: () => void;
  displayName: string;
};

function ZendeskConfigLoginForm(
  { name, loginConfigList, ...props }: ZendeskConfigLoginFormProps,
  ref: any,
) {
  const [auth, setAuth] = useState(() => props.config?.tokenType ?? "default");
  const isEdit = !_.isUndefined(props.config);
  const [form] = Form.useForm();

  const onOk = async (data: any) => {
    await form.validateFields();
    try {
      const payload = {
        ...data,
        ...(auth === "ACCESS_TOKEN" ? { tokenType: auth } : {}),
        ...(auth === "default" ? { loginType: "TOKEN" } : {}),
      };
      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ]("ZENDESK", payload, props.config?.id).promise;

      message.success(`${name} Login Configuration created`);
      if (!isEdit) {
        ModalUtil.show({
          content: (
            <ZenDeskConfigMappingForm
              onUpdateConfigComponent={props.onUpdateConfigComponent}
              name={name}
              loginConfigId={response.id}
            />
          ),
        });
      }
      props?.onUpdateConfigComponent(true);
    } catch (err: any) {
      console.log(err);
      if (!_.isEmpty(err.response.data.errors)) {
        const { field, defaultMessage } = err.response.data.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      return Promise.reject(err.response?.data?.message);
    }
  };

  const authRadio = useMemo(
    () => (
      <Radio.Group
        onChange={(e) => setAuth(e.target.value)}
        value={auth}
        className="m-b-l"
      >
        <Radio value={"default"}>API Token</Radio>
        <Radio value={"ACCESS_TOKEN"}>Client OAuth Token</Radio>
      </Radio.Group>
    ),
    [auth],
  );

  const modalProps = {
    title: (
      <>
        {isEdit ? "Edit" : "Add"} {props.displayName} Login Credentials{" "}
      </>
    ),
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  const fields = [
    {
      type: "Label",
      value: authRadio,
    },
    {
      name: "name",
      label: "Name",
      colSpan: 12,
      props: {
        autoFocus: true,
      },
      required: true,
      rules: [
        duplicateNameValidator({ list: loginConfigList }),
        nameRegexValidator,
      ],
    },
    {
      name: "url",
      label: "URL",
      colSpan: 12,
      rules: [urlRegexValidator],
      required: true,
    },
    {
      name: "email",
      label: "Email",
      rules: [emailValidator],
      colSpan: 12,
      required: true,
    },
    {
      name: "apiToken",
      label: auth === "default" ? "API Token" : "Client Token",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    {
      name: "version",
      label: "Version",
      colSpan: 12,
      disabled: true,
    },
  ];

  const ModalFormComponent = BaseModalForm;

  return (
    <ModalFormComponent
      modalProps={modalProps}
      ref={ref}
      afterClose={props.afterClose}
      initialValues={{ ...props.config, version: "V3" }}
      form={form}
      fields={fields}
      onOk={onOk}
      title={modalProps.title}
    />
  );
}

export default forwardRef(ZendeskConfigLoginForm);
